import { useRef, type ReactElement } from 'react';
import { SPUR_FLACH_BIS, useEditor } from '../store';

/**
 * Der Griff der Zeitleiste: ein Balken oben auf der Zeitleisten-Karte.
 *
 * Er sass bis zum 7. September in der Transportleiste, weil dort noch Platz
 * war. Im Entwurf gehoert er dorthin, wo er wirkt -- an die Oberkante dessen,
 * was er groesser und kleiner macht. Sascha am 7. September: "der Anfasser zum
 * Hoch- und Runterziehen der Timeline skaliert die Timeline irgendwie"; auf
 * der Karte ist unmissverstaendlich, was am Griff haengt.
 *
 * Ziehen setzt die Hoehe, ein Tipp ohne Weg schaltet zwischen flach und voll
 * um -- der schnelle Weg zur grossen Leinwand bleibt einer.
 */
export function Griff(): ReactElement {
  const spurHoehe = useEditor((s) => s.spurHoehe);
  const setSpurHoehe = useEditor((s) => s.setSpurHoehe);
  const umschalten = useEditor((s) => s.zeitleisteUmschalten);
  /**
   * Wie viele Spuren an dem Griff haengen.
   *
   * Der Griff stellt die Hoehe *einer* Spur, die Karte ist so hoch wie alle
   * zusammen. Ohne diese Zahl wanderte die Kante bei vier Spuren viermal so
   * weit wie der Finger. (Sascha, 7. September: „der Anfasser macht das total
   * unproportional zu meinem Finger“.)
   */
  const spuren = useEditor((s) => Math.max(1, s.timeline.tracks.length));
  const klein = spurHoehe < SPUR_FLACH_BIS;
  /** Ein angefangener Zug. Ohne Weg bleibt es ein Tipp. */
  const zug = useRef<{ pointerId: number; y: number; start: number; gezogen: boolean } | null>(null);

  return (
    <button
      className={`griff ${klein ? 'active' : ''}`.trim()}
      onPointerDown={(e) => {
        zug.current = { pointerId: e.pointerId, y: e.clientY, start: spurHoehe, gezogen: false };
        e.currentTarget.setPointerCapture(e.pointerId);
      }}
      onPointerMove={(e) => {
        const z = zug.current;
        if (!z || z.pointerId !== e.pointerId) return;
        // Nach oben ziehen macht die Spuren hoeher: der Griff sitzt an der
        // Oberkante, und die Karte waechst nach oben ins Bild hinein.
        const dy = z.y - e.clientY;
        if (!z.gezogen && Math.abs(dy) < 6) return;
        z.gezogen = true;
        // Geteilt durch die Zahl der Spuren: so wandert die Kante der Karte
        // genau so weit wie der Finger, nicht ein Vielfaches davon.
        setSpurHoehe(z.start + dy / spuren);
      }}
      onPointerUp={(e) => {
        const z = zug.current;
        zug.current = null;
        if (!z || z.pointerId !== e.pointerId || z.gezogen) return;
        umschalten();
      }}
      onPointerCancel={() => {
        zug.current = null;
      }}
      aria-label={klein ? 'Zeitleiste größer' : 'Zeitleiste kleiner'}
      aria-pressed={klein}
      title="Tippen: flach oder voll. Ziehen: eigene Höhe."
      data-zeitleiste
      data-spurhoehe={spurHoehe}
    >
      <span className="balken" aria-hidden="true" />
    </button>
  );
}
